import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Trash2, RotateCcw } from 'react-feather';
import { InlineSelect, InlineMultiSelect, StatusDot, SortIcon } from '../../components/InlineTable';
import { ConfigToolbar, ToolbarButton, ConfigEmptyState, ConfirmPopover } from '../../components/ConfigPages';
import { ROOM_STAT_MAPPED_TYPES } from '../../utils/mappedTypes';
import {
  deleteRoomStatType, restoreRoomStatType, getRoomStatMappedTypes, updateRoomStatMappedType, replaceRoomStatMappedTypes,
  getRoomStatAccountMappings, updateRoomStatAccountMapping, replaceRoomStatAccountMappings,
} from '../../utils/api';
import {
  OPERATION_OPTIONS, ACCOUNT_OPERATION_OPTIONS, COA_SOURCED_STAT_TYPES, buildCoaOptions, buildCoaMap, coaLabel,
  useSortConfig, sortRows,
} from './revenueMapShared';

const STAT_OPTIONS = ROOM_STAT_MAPPED_TYPES.map((v) => ({ value: v, label: v }));
// Legacy rows carry 'Add'/'Subtract' capitalised; the vocabulary (and the API) is lowercase.
const op = (v) => String(v || 'add').trim().toLowerCase();

export function describeRoomStatType(t) {
  if (!t) return { state: 'none', title: '', unmapped: false };
  if (t.isDeleted) return { state: 'none', title: 'Deleted — ignored by every stat rollup', unmapped: false };
  const mappings = t.mappings || [];
  if (mappings.length === 0) return { state: 'partial', title: 'No mapped stat — never counted toward occupancy, ADR or RevPAR', unmapped: true };
  return {
    state: 'complete',
    title: `Mapped to ${mappings.map((m) => `${m.mappedType} (${op(m.operation)})`).join(', ')}`,
    unmapped: false,
  };
}

/**
 * RoomStatsTab — the "Room stats" tab of the Revenue map page.
 * PMS stat type → one or more canonical stats (add/subtract/replace); Available/Occupied
 * Rooms may additionally be sourced from statistical COA accounts.
 * Mappings ride on each row (`getRoomStatTypes(..., includeMappings=true)`).
 */
export default function RoomStatsTab({
  orgId,
  locationId,
  roomStatTypes,
  setRoomStatTypes,
  accountMappings,
  setAccountMappings,
  chartAccounts,
  showDeleted,
  setShowDeleted,
  statusFilter,
  setStatusFilter,
  refreshRoomStatTypes,
  busy = false,
}) {
  const [search, setSearch] = useState('');
  const [sortConfig, requestSort] = useSortConfig('name');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const coaOptions = useMemo(() => buildCoaOptions(chartAccounts), [chartAccounts]);
  const coaMap = useMemo(() => buildCoaMap(chartAccounts), [chartAccounts]);

  const live = useMemo(() => roomStatTypes.filter((t) => !t.isDeleted), [roomStatTypes]);
  const counts = useMemo(() => {
    const unmapped = live.filter((t) => (t.mappings || []).length === 0).length;
    return { all: live.length, unmapped, mapped: live.length - unmapped, deleted: roomStatTypes.length - live.length };
  }, [live, roomStatTypes.length]);

  const rows = useMemo(() => {
    let list = roomStatTypes;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((t) => t.name?.toLowerCase().includes(q) || (t.mappings || []).some((m) => m.mappedType?.toLowerCase().includes(q)));
    }
    if (statusFilter === 'unmapped') list = list.filter((t) => t.isDeleted || (t.mappings || []).length === 0);
    if (statusFilter === 'mapped') list = list.filter((t) => !t.isDeleted && (t.mappings || []).length > 0);
    return sortRows(list, sortConfig, (t, key) => {
      if (key === 'mapped') return (t.mappings || []).map((m) => m.mappedType).sort().join(', ') || null;
      if (key === 'status') return (t.mappings || []).length > 0 ? 1 : 0;
      return t[key] ?? null;
    });
  }, [roomStatTypes, search, statusFilter, sortConfig]);

  const setMappingsLocal = useCallback((roomStatTypeId, mappings) => {
    setRoomStatTypes((prev) => prev.map((t) => (t.id === roomStatTypeId ? { ...t, mappings } : t)));
  }, [setRoomStatTypes]);

  // ── Mapped stats: one replace call ──────────────────────────
  const setMappedTypes = useCallback(async (t, values) => {
    const before = t.mappings || [];
    const beforeVals = before.map((m) => m.mappedType);
    if (beforeVals.length === values.length && beforeVals.every((v) => values.includes(v))) return;
    const next = values.map((mappedType) => {
      const kept = before.find((m) => m.mappedType === mappedType);
      return kept || { id: `tmp-${mappedType}`, roomStatTypeId: t.id, mappedType, operation: 'add' };
    });
    setMappingsLocal(t.id, next);
    try {
      const res = await replaceRoomStatMappedTypes({
        roomStatTypeId: t.id, orgId, locationId,
        mappedTypes: next.map((m) => ({ mappedType: m.mappedType, operation: op(m.operation) })),
      });
      const rowsBack = Array.isArray(res) ? res : (Array.isArray(res?.mappings) ? res.mappings : null);
      const fresh = rowsBack || (await getRoomStatMappedTypes(t.id)) || [];
      setMappingsLocal(t.id, fresh.filter((m) => !m.isDeleted));
      toast.success(`Mapped stats saved for ${t.name}`);
    } catch (err) {
      console.error('Error replacing room stat mapped types:', err);
      setMappingsLocal(t.id, before);
      toast.error(`Could not save mapped stats for ${t.name} — try again.`);
    }
  }, [orgId, locationId, setMappingsLocal]);

  const setOperation = useCallback(async (t, mapping, operation) => {
    const before = op(mapping.operation);
    if (before === operation) return;
    setMappingsLocal(t.id, (t.mappings || []).map((m) => (m.id === mapping.id ? { ...m, operation } : m)));
    try {
      await updateRoomStatMappedType(mapping.id, { operation });
      toast.success(`${mapping.mappedType}: ${operation}`);
    } catch (err) {
      console.error('Error updating operation:', err);
      setMappingsLocal(t.id, (t.mappings || []).map((m) => (m.id === mapping.id ? { ...m, operation: before } : m)));
      toast.error(`Could not change the operation for ${mapping.mappedType} — try again.`);
    }
  }, [setMappingsLocal]);

  // ── COA sources for Available/Occupied Rooms: one replace call ─
  const setAccountCoas = useCallback(async (mapping, chartOfAccountIds) => {
    const before = accountMappings[mapping.id] || [];
    const beforeIds = before.map((m) => m.chartOfAccountId);
    if (beforeIds.length === chartOfAccountIds.length && beforeIds.every((v) => chartOfAccountIds.includes(v))) return;
    const next = chartOfAccountIds.map((cid) => before.find((m) => m.chartOfAccountId === cid) || { id: `tmp-${cid}`, roomStatMappedTypeId: mapping.id, chartOfAccountId: cid, operation: 'add' });
    setAccountMappings((prev) => ({ ...prev, [mapping.id]: next }));
    try {
      const res = await replaceRoomStatAccountMappings({
        roomStatMappedTypeId: mapping.id, orgId, locationId,
        mappings: next.map((m) => ({ chartOfAccountId: m.chartOfAccountId, operation: op(m.operation) })),
      });
      const rowsBack = Array.isArray(res) ? res : (Array.isArray(res?.mappings) ? res.mappings : null);
      let fresh = rowsBack;
      if (!fresh) {
        const all = await getRoomStatAccountMappings(orgId, locationId);
        fresh = (all || []).filter((m) => m.roomStatMappedTypeId === mapping.id);
      }
      setAccountMappings((prev) => ({ ...prev, [mapping.id]: fresh }));
      toast.success(`COA sources saved for ${mapping.mappedType}`);
    } catch (err) {
      console.error('Error replacing room stat account mappings:', err);
      setAccountMappings((prev) => ({ ...prev, [mapping.id]: before }));
      toast.error(`Could not save COA sources for ${mapping.mappedType} — try again.`);
    }
  }, [accountMappings, setAccountMappings, orgId, locationId]);

  const setAccountOperation = useCallback(async (mapping, acct, operation) => {
    const before = op(acct.operation);
    if (before === operation) return;
    const patch = (op) => setAccountMappings((prev) => ({ ...prev, [mapping.id]: (prev[mapping.id] || []).map((m) => (m.id === acct.id ? { ...m, operation: op } : m)) }));
    patch(operation);
    try {
      await updateRoomStatAccountMapping(acct.id, { operation });
      toast.success(`${coaLabel(coaMap, acct.chartOfAccountId).split(' - ')[0]}: ${operation}`);
    } catch (err) {
      console.error('Error updating account operation:', err);
      patch(before);
      toast.error('Could not change the operation — try again.');
    }
  }, [setAccountMappings, coaMap]);

  // ── Delete / restore ────────────────────────────────────────
  const confirmDelete = async () => {
    const t = roomStatTypes.find((x) => x.id === deleteId);
    if (!t) return;
    setDeleting(true);
    try {
      await deleteRoomStatType(t.id);
      toast.success(`Deleted "${t.name}"`);
      setDeleteId(null);
      if (refreshRoomStatTypes) await refreshRoomStatTypes();
    } catch (err) {
      console.error('Error deleting room stat type:', err);
      toast.error(`Could not delete "${t.name}" — try again.`);
    } finally {
      setDeleting(false);
    }
  };
  const restore = async (t) => {
    try {
      await restoreRoomStatType(t.id, orgId);
      toast.success(`Restored "${t.name}"`);
      if (refreshRoomStatTypes) await refreshRoomStatTypes();
    } catch (err) {
      console.error('Error restoring room stat type:', err);
      toast.error(`Could not restore "${t.name}" — try again.`);
    }
  };

  const th = (key, label, style) => (
    <th className="it-sort" style={style} onClick={() => requestSort(key)}>
      {label}<SortIcon columnKey={key} sortKey={sortConfig.key} direction={sortConfig.direction} />
    </th>
  );

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: search, onChange: setSearch, placeholder: 'Search stat type or mapped stat…' }}
        status={{ value: statusFilter, counts, onChange: setStatusFilter }}
        showDeleted={{ value: showDeleted, onChange: setShowDeleted, count: showDeleted ? counts.deleted : undefined }}
        meta={`${rows.length} of ${counts.all}`}
      />
      <div className="it-scroll">
        <table className="it-tbl" style={{ minWidth: 1100 }}>
          <colgroup>
            <col style={{ width: 260 }} />
            <col style={{ width: 340 }} />
            <col />
            <col style={{ width: 80 }} />
            <col style={{ width: 96 }} />
          </colgroup>
          <thead>
            <tr>
              {th('name', 'Stat type')}
              {th('mapped', 'Mapped stats')}
              <th>Operations · COA sources</th>
              {th('status', 'Status', { textAlign: 'center' })}
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const d = describeRoomStatType(t);
              const disabled = busy || !!t.isDeleted;
              const mappings = t.mappings || [];
              return (
                <tr key={t.id} className={`cp-row${t.isDeleted ? ' it-row-deleted' : ' it-row-hover'}`}>
                  <td>
                    <div className="cp-cell">
                      <div className="cp-name" title={t.name || ''}>{t.name || '—'}</div>
                      {t.isDeleted ? <div className="cp-code">Deleted</div> : null}
                    </div>
                  </td>
                  <td style={{ verticalAlign: 'top', paddingTop: mappings.length > 1 ? 6 : 0 }}>
                    <InlineMultiSelect
                      options={STAT_OPTIONS}
                      value={mappings.map((m) => ({ value: m.mappedType, label: m.mappedType }))}
                      onChange={(opts) => setMappedTypes(t, (opts || []).map((o) => o.value))}
                      placeholder="Select mapped stats…"
                      rowActive disabled={disabled} minWidth={320}
                      ariaLabel={`Mapped stats for ${t.name}`}
                    />
                  </td>
                  <td style={{ padding: mappings.length ? '6px 0' : 0 }}>
                    {mappings.length === 0 ? (
                      <span className="cp-cell cp-help">No mappings</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {mappings.map((m) => {
                          const accts = accountMappings[m.id] || [];
                          const coaSourced = COA_SOURCED_STAT_TYPES.has(m.mappedType);
                          return (
                            <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                              <span className="cp-cell cp-mono" style={{ minWidth: 150, lineHeight: '30px', color: 'var(--lodge-onyx-600)' }}>{m.mappedType}</span>
                              <div style={{ width: 130 }}>
                                <InlineSelect value={op(m.operation)} options={OPERATION_OPTIONS} onChange={(v) => setOperation(t, m, v || 'add')} rowActive clearable={false} disabled={disabled || String(m.id).startsWith('tmp-')} minWidth={180} ariaLabel={`Operation for ${m.mappedType}`} />
                              </div>
                              {coaSourced && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 320 }}>
                                  <InlineMultiSelect
                                    options={coaOptions}
                                    value={accts.map((a) => coaOptions.find((o) => o.value === a.chartOfAccountId) || { value: a.chartOfAccountId, label: `Account ${a.chartOfAccountId}` })}
                                    onChange={(opts) => setAccountCoas(m, (opts || []).map((o) => o.value))}
                                    placeholder="COA source account(s)…"
                                    rowActive disabled={disabled || String(m.id).startsWith('tmp-')} minWidth={420}
                                    ariaLabel={`COA sources for ${m.mappedType}`}
                                  />
                                  {accts.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 12 }}>
                                      {accts.map((a) => {
                                        const label = coaLabel(coaMap, a.chartOfAccountId);
                                        return (
                                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span className="cp-code" title={label} style={{ minWidth: 90, marginTop: 0 }}>{label.split(' - ')[0]}</span>
                                            <div style={{ width: 120 }}>
                                              <InlineSelect value={op(a.operation)} options={ACCOUNT_OPERATION_OPTIONS} onChange={(v) => setAccountOperation(m, a, v || 'add')} rowActive clearable={false} disabled={disabled || String(a.id).startsWith('tmp-')} minWidth={170} ariaLabel={`Operation for ${label}`} />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td><StatusDot state={d.state} title={d.title} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="cp-cell" style={{ position: 'relative', display: 'inline-block' }}>
                      {t.isDeleted ? (
                        <ToolbarButton small variant="ghost" icon={RotateCcw} onClick={() => restore(t)} title="Restore this stat type">Restore</ToolbarButton>
                      ) : (
                        <div className="cp-row-actions">
                          <ToolbarButton small iconOnly variant="ghost" icon={Trash2} onClick={() => setDeleteId(t.id)} aria-label={`Delete ${t.name}`} title="Delete stat type" />
                        </div>
                      )}
                      <ConfirmPopover open={deleteId === t.id} onClose={() => setDeleteId(null)} onConfirm={confirmDelete} title={`Delete "${t.name}"?`} body="Its stats stop rolling up; restore it from Show deleted." confirmLabel="Delete" busy={deleting} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {roomStatTypes.length === 0 && !busy && (
          <ConfigEmptyState title="No stat types here yet" body="They arrive with the first PMS statistics file for this location." />
        )}
        {roomStatTypes.length > 0 && rows.length === 0 && (
          <ConfigEmptyState title="Nothing matches" body="No stat types match this search or filter." action={<ToolbarButton variant="ghost" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Clear filters</ToolbarButton>} />
        )}
      </div>
    </div>
  );
}
